import { useState } from "react";
import { Badge } from "src/components/ui/Badge";
import { useLeaveMutation } from "../hooks/useLeaveMutation";
import { LeaveRequest } from "src/redux/types/_attendance.type";
import { Button } from "src/components/buttons/shadcn-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import { AlertTriangle, PencilLine } from "lucide-react";
import EditLeaveDialog from "./EditLeaveDialog";
import { DocumentPreview } from "./DocumentPreview";

function DetailCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-lg border bg-white shadow-sm">
      <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">
        {label}
      </p>
      <p className="text-sm font-medium text-slate-700 break-words">{value}</p>
    </div>
  );
}

const DOCUMENT_REQUIRED_TYPES = ["MTL", "PTL"];

const requiresDocument = (leave: LeaveRequest) => {
  if (DOCUMENT_REQUIRED_TYPES.includes(leave?.type)) return true;
  if (leave?.type === "SL" && Number(leave?.days) > 1) return true;
  return false;
};

const statusColors: Record<LeaveRequest["status"], string> = {
  APPROVED: "bg-green-100 text-green-700 border-green-300",
  PENDING: "bg-yellow-100 text-yellow-700 border-yellow-300",
  REJECTED: "bg-red-100 text-red-700 border-red-300",
};

const rejectionReasons = [
  "Documents not clear",
  "Document not submitted",
  "Insufficient Leave balance",
  "Business / Workload Requirements",
  "Short Notice",
  "Policy Violation",
  "Unjustified Leave Reason",
];

const leaveType: Record<string, string> = {
  CL: "Casual Leave",
  SL: "Sick Leave",
  PLL: "Planned Leave",
  MTL: "Maternity Leave",
  PTL: "Paternity Leave",
  LWP: "Leave Without Pay",
};

const LeaveDetails = ({
  leave,
  balance,
}: {
  leave: LeaveRequest;
  balance: any;
}) => {

  const docRequired = requiresDocument(leave);

  const [showReasonSelect, setShowReasonSelect] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");

  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(!open);
  };

  const { mutate: statusChange, isPending } = useLeaveMutation();

  const handleRequest = (status: "APPROVED" | "REJECTED") => {
    statusChange(
      { id: leave.id, status, reason: selectedReason },
      {
        onSuccess: () => {
          setShowReasonSelect(false);
          setSelectedReason("");
        },
      },
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 relative">
        <div className="absolute top-3 right-3">
          <button
            className="rounded-full p-2 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 ease-in-out hover:bg-gray-200 bg-gray-100/20 backdrop-blur-md"
            onClick={() => setOpen(!open)}
          >
            <PencilLine className="size-3.5" />
          </button>
        </div>

        <div>
          <p className="text-base font-medium text-slate-800 capitalize">
            {leave.employee_name}
          </p>
          <p className="text-sm text-slate-500">{leave.post}</p>
          <p className="text-sm text-slate-400">{leave.employee_email}</p>
          <p className="text-sm text-slate-400 mt-2">
            Requested On: {new Date(leave.created_at).toLocaleDateString()}
          </p>
          <p className="text-sm text-slate-400 mt-2">Days: {leave.days}</p>
        </div>
        <div>
          <Badge
            className={`${statusColors[leave.status]} px-3 py-1 rounded-full`}
          >
            {leave.status}
          </Badge>
        </div>
      </div>

      {/* Details */}
      <div className="flex gap-3">
        <div className="grow">
          <DetailCard label="Leave Type" value={leaveType[leave.type]} />
        </div>
        <div className="flex gap-3">
          <DetailCard label="Used" value={balance?.used} />
          <DetailCard label="Remaining" value={balance?.remaining} />
        </div>
      </div>
      <DetailCard label="Reason" value={leave.reason} />
      <div className="grid grid-cols-2 gap-4 text-sm">
        <DetailCard
          label="From"
          value={new Date(leave.from_date).toLocaleDateString()}
        />
        <DetailCard
          label="To"
          value={new Date(leave.to_date).toLocaleDateString()}
        />
      </div>
      {leave.rejection_reason && (
        <DetailCard label="Rejection Reason" value={leave.rejection_reason} />
      )}

      <div className="flex items-center justify-between gap-4 my-8">
        {/* Accept Button */}
        <Button
          className="hover:bg-success hover:text-foreground bg-green-50 text-green-500 w-32"
          onClick={() => handleRequest("APPROVED")}
          disabled={isPending}
        >
          {isPending ? (
            <span className="loading loading-dots loading-sm"></span>
          ) : (
            "Approve"
          )}
        </Button>

        {!showReasonSelect ? (
          <Button
            className="hover:bg-error hover:text-foreground bg-red-50 text-red-500 w-32"
            onClick={() => setShowReasonSelect(true)}
          >
            Reject
          </Button>
        ) : (
          <div className="flex gap-3 justify-evenly items-center w-full">
            <Select onValueChange={(value) => setSelectedReason(value)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {rejectionReasons.map((reason, idx) => (
                  <SelectItem key={idx} value={reason}>
                    {reason}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              className="hover:bg-error hover:text-foreground bg-red-50 text-red-500 w-32"
              onClick={() => handleRequest("REJECTED")}
              disabled={isPending || !selectedReason}
            >
              {isPending ? (
                <span className="loading loading-dots loading-sm"></span>
              ) : (
                "Confirm Reject"
              )}
            </Button>
          </div>
        )}

        {/* <Button
          className="hover:bg-success hover:text-foreground bg-amber-50 text-amber-700 w-32"
          onClick={() => {
            toast.success("Forwarded to MD!");
          }}
          disabled={isPending}
        >
          {isPending ? (
            <span className="loading loading-dots loading-sm"></span>
          ) : (
            "Forwar to MD"
          )}
        </Button> */}
      </div>
      {/* uploaded media view */}
      {docRequired && (
        <div className="mt-4">
          {leave.documentation ? (
            <DocumentPreview url={leave.documentation} />
          ) : (
            <div className="flex items-center gap-2 rounded-xl border border-dashed border-amber-300 bg-amber-50 p-3">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
              <p className="text-xs text-amber-700">
                No supporting document uploaded for this leave type.
              </p>
            </div>
          )}
        </div>
      )}

      <EditLeaveDialog leave={leave} open={open} close={handleClose} />
    </div>
  );
};

export default LeaveDetails;
